import "../css/app.css";
import { ProgCss } from "./ProgCss.Generated";
import { AppState, InitPjax, doLoadingPageRequest, syncHistoryWithNewUrl, getBrowserTabId } from "./Pjax";
import { UnsupportedBrowserModal, ReplaceFullPageWithError } from "./UnsupportedBrowserModal";
import { addErrorHandler } from "./addErrorHandler";
import { ProxyHttpRequestFunctions, RedirectTo } from "./countedFetch";
import { AllowHidingTestDbWarning } from "./environment-warning";
import { showLoadingIndicator } from "./loaders";
import { AssertNotNull, cast, castOrNull, ConditionalLog, } from "./tools";
import { GetOpenedClickableTooltipsCount, StartTooltipsModule } from "./tooltips";
import { ProgPerformanceTiming, logNavigationTiming } from "./debugInfo";
import { wireUpProgressJsControlsWithin, } from "./wireUpProgressJsControlsWithin";
import { registerSessionPinger } from "./pingSession";
import { InitTextInputAutoSelect } from "./TextInputAutoSelect";
import { InitDateFieldHandling } from "./DateFieldHandling";
import { WireUpWarningBars } from "./warningBar";
import { registerStudentAanmeldingSupplementListeners } from "./portal/StudentAanmeldingSupplementVraag";
import { registerPro6WorkflowFormListeners, registerPortaalIntekenImportFormListeners } from "./portal/admin";
import { registerDocentCijferLijstFormListener, registerDocentDocentFormListener } from "./portal/docent";
import { registerPro6WorkflowListeners } from "./portal/workflow";
import { vertaal } from "./vertaal";
import { SemanticSymbol } from "./SemanticSymbols.Generated";

export const wiredUpMarkerSymbol = Symbol("wiredUpMarkerSymbol");

enum RedirectState {
    OpenInNewWindow = 'OpenInNewWindow',
    OpenInCurrentWindow = 'OpenInCurrentWindow',
    TriggerFileDownload = 'TriggerFileDownload',
    OpenInCurrentWindowOrDownload = 'OpenInCurrentWindowOrDownload',
}

listenForEscapeKeyPresses();
ImproveKeyboardAndFocusHandling();
ProxyHttpRequestFunctions();
addErrorHandler();
UnsupportedBrowserModal();
StartTooltipsModule();
InitTextInputAutoSelect();
InitDateFieldHandling();
registerScrollToTopButtonListeners();
registerSessionPinger();
InitAutoSubmits();
InitPjax();
OnPageLoadOrPjax();

declare global {
    interface Element {
        [wiredUpMarkerSymbol]: true | undefined
    }
}

export function OnPageLoadOrPjax(pjaxLoadEventDetails?: { perf: ProgPerformanceTiming, location: URL, allowPushState: boolean }) {
    const netLocation = pjaxLoadEventDetails?.location ?? new URL(location.href);
    WireUpWarningBars();
    if (UnsupportedBrowserModal())
        return;

    document.body[wiredUpMarkerSymbol] = undefined;
    document.documentElement.removeAttribute("data-debugshowids");

    let appState = AppState();
    if (!appState) {
        ReplaceFullPageWithError("Interne fout", "Internal error",
            "Er is een onbekende fout opgetreden. Uw kunt proberen de pagina te verversen, cookies te legen, of later terug te keren.",
            "An unknown error occured. You may try to refresh this page, clear your cookies and cache, or try again later.");

        throw new Error("Missing AppState");
    }
    CopyAppStateToHtmlDocument(appState);
    if (appState.getAttribute("data-is-loading-page") != null) {
        doLoadingPageRequest(netLocation);
        return;
    }

    ConditionalLog.log("post-pjax status " + getBrowserTabId(appState) + " on " + location.href);
    processPotentialRedirects(appState);
    syncHistoryOnPjaxLoad(appState, pjaxLoadEventDetails?.allowPushState ?? false, netLocation);
    AllowHidingTestDbWarning();
    wireUpProgressJsControlsWithin(document.body);
    wireUpProgressJsControlsThatAreNeverInPopovers(document.body);
    printWhenInAspEditPortBasePrintMode();
    logNavigationTiming(pjaxLoadEventDetails?.perf || performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming);
}

function syncHistoryOnPjaxLoad(appState: HTMLElement, allowPushState: boolean, netLocation: Location | URL) {
    const appStateUri = appState.getAttribute("data-url");
    const uri = appStateUri ? netLocation.origin + appStateUri : netLocation.href;
    syncHistoryWithNewUrl(uri, allowPushState || !!appStateUri);
}

function processPotentialRedirects(appState: HTMLElement) {
    const redirect = appState.getAttribute("data-redirect");
    const redirectState = RedirectState[appState.getAttribute("data-redirect-state") as keyof typeof RedirectState];
    if (redirect && redirectState) {
        if (redirectState == RedirectState.OpenInNewWindow) {
            open(redirect, "_blank", "noreferrer");
        } else if (redirectState == RedirectState.OpenInCurrentWindow) {
            showLoadingIndicator();
            RedirectTo(redirect);
        } else if (redirectState == RedirectState.TriggerFileDownload || redirectState == RedirectState.OpenInCurrentWindowOrDownload) {
            ConditionalLog.log("attempting to trigger file download: ", redirect);
            var tmpLink = document.createElement("a");
            tmpLink.href = redirect;
            if (redirectState == RedirectState.TriggerFileDownload) {
                tmpLink.download = "";
            }
            tmpLink.click();
            //WAS: window.location.href = redirect; but the above works in chrome and FF at least
        } else {
            throw new Error("Unexpected redirectState: " + redirectState);
        }
    }
}

function wireUpProgressJsControlsThatAreNeverInPopovers(scopeEl: HTMLElement) {
    registerDocentCijferLijstFormListener(scopeEl);
    registerDocentDocentFormListener(scopeEl);
    registerPro6WorkflowFormListeners(scopeEl);
    registerPro6WorkflowListeners(scopeEl);
    registerPortaalIntekenImportFormListeners(scopeEl);
    registerPortaalIntekenListeners(scopeEl);
    registerSortButtonListeners(scopeEl);
    registerDateInputListeners(scopeEl);
    registerDataOnclickConfirmMessageListeners(scopeEl);
    registerPortaalIntekenenToestemmingListeners(scopeEl);
    registerGenericPrintListeners(scopeEl);
    registerExpandableRowListeners(scopeEl);
    registerJsPrintButtonListeners(scopeEl);
    registerJsNeedsValidationListeners(scopeEl);
    registerJsSelectAllListeners(scopeEl);
    registerInvertSelectionListeners(scopeEl);
    registerGridOpenLeftListeners(scopeEl);
    registerStudentAanmeldingSupplementListeners(scopeEl);
    registerCatalogusExpandableListeners(scopeEl);
    registerCatalogusToggleFilteringListeners(scopeEl);
}

function registerCatalogusToggleFilteringListeners(scopeEl: HTMLElement) {
    for (const button of scopeEl.getElementsByClassName(ProgCss.js_Catalogus_toggleFiltering)) {
        if (button instanceof HTMLElement) {
            button.addEventListener("click", () => document.querySelector(`.${ProgCss.Catalogus_homepageContainerLeft}`)?.classList.toggle(ProgCss.is_shown));
        }
    }
}

function registerCatalogusExpandableListeners(scopeEl: HTMLElement) {
    for (const element of scopeEl.getElementsByClassName(ProgCss.Catalogus_expandable)) {
        element.addEventListener("click", evt => cast(evt.target, HTMLElement).classList.toggle(ProgCss.is_hidden));
    }
}

export function registerScrollToTopButtonListeners() {
    document.addEventListener("scroll", () => {
        const scrollToTopButtons = document.getElementsByClassName(ProgCss.Catalogus_scrollToTop);

        if (scrollToTopButtons.length) {
            const rootEl = document.documentElement;
            const showScrollToTopButton = rootEl.scrollTop / (rootEl.scrollHeight - rootEl.clientHeight) > 0.3;
            for (const button of scrollToTopButtons) {
                button.classList.toggle(ProgCss.is_shown, showScrollToTopButton);
            }
        }
    }, { passive: true });
}

function printWhenInAspEditPortBasePrintMode() {
    if (new URLSearchParams(location.search).get('ge_print') === 'ja') {
        addEventListener("afterprint",
            () => { close(); }
        );
        print();
    }
}

function registerGridOpenLeftListeners(scopeEl: HTMLElement) {
    scopeEl.getElementsByClassName(ProgCss.js_open_left)[0]?.addEventListener("click", evt => {
        evt.preventDefault();
        scopeEl.getElementsByClassName(ProgCss.PnetPageGrid)[0]?.classList.toggle(ProgCss.is_collapsed);
    });
}

function registerInvertSelectionListeners(scopeEl: HTMLElement) {
    for (const button of scopeEl.getElementsByClassName(ProgCss.js_invert_selection)) {
        button.addEventListener("click", evt => {
            evt.preventDefault();
            const container = cast(evt.currentTarget, Element).getAttribute("data-container");
            if (container) {
                for (const checkbox of document.getElementById(container)?.querySelectorAll("input[type=checkbox]") ?? []) {
                    const castedCheckbox = cast(checkbox, HTMLInputElement);
                    castedCheckbox.checked = !castedCheckbox.checked;
                }
            }
        });
    }
}

function registerJsSelectAllListeners(scopeEl: HTMLElement) {
    for (const button of scopeEl.getElementsByClassName(ProgCss.js_select_all)) {
        button.addEventListener("click", evt => {
            evt.preventDefault();
            const container = cast(evt.currentTarget, Element).getAttribute("data-container");
            if (container) {
                for (const checkbox of document.getElementById(container)?.querySelectorAll("input[type=checkbox]") ?? []) {
                    cast(checkbox, HTMLInputElement).checked = true;
                }
            }
        });
    }
}

function registerJsNeedsValidationListeners(scopeEl: HTMLElement) {
    for (const element of scopeEl.querySelectorAll(`form.${ProgCss.js_needs_validation}`)) {
        const form = cast(element, HTMLFormElement);

        form.addEventListener("submit", evt => {
            if (form.checkValidity() === false) {
                evt.preventDefault();
                evt.stopPropagation();
            }

            form.classList.add(ProgCss.was_validated);
        });
    }
}

function registerJsPrintButtonListeners(scopeEl: HTMLElement) {
    for (const button of scopeEl.getElementsByClassName(ProgCss.js_print)) {
        button.addEventListener("click", event => {
            event.preventDefault();

            const containersToHide = cast(event.currentTarget, HTMLElement).getAttribute("data-containers-hide");
            if (containersToHide) {
                cast(document.getElementById(containersToHide), HTMLElement).classList.add(ProgCss.js_prg_niet_printen);
            }

            const containersToShow = cast(event.currentTarget, HTMLElement).getAttribute("data-containers-show");
            if (containersToShow) {
                cast(document.getElementById(containersToShow), HTMLElement).classList.remove(ProgCss.js_prg_niet_printen);
            }

            print();
        });
    }
}

function registerExpandableRowListeners(scopeEl: HTMLElement) {
    for (const rowElement of scopeEl.getElementsByClassName(ProgCss.ExpandableRow)) {
        rowElement.addEventListener("click", (rowElementEvt) => {
            const expandableRow = cast(rowElementEvt.currentTarget, HTMLTableRowElement);
            const possibleExpandIcons = expandableRow.getElementsByClassName(ProgCss.ExpandableRow_expanderButton);
            const expandableIcon = possibleExpandIcons.length > 0 ? possibleExpandIcons[0] : null;
            const clickedOnExpandIcon = expandableIcon != null && expandableIcon.contains(rowElementEvt.target as Node);

            if (clickedOnExpandIcon) {
                expandableRow.classList.toggle(ProgCss.is_expanded);
                rowElementEvt.preventDefault();
            }

            for (const element of cast(expandableRow.nextElementSibling, HTMLTableRowElement).getElementsByClassName(ProgCss.js_cancel_expanded_row)) {
                const cancelElement = cast(element, HTMLElement);
                cancelElement.addEventListener("click", (cancelElementEvt) => {
                    expandableRow.classList.toggle(ProgCss.is_expanded);
                    cancelElementEvt.preventDefault();
                }, { once: true });
            }
        });
    }
}

function InitAutoSubmits() {
    document.addEventListener("change", evt => {
        const triggerEl = evt.target;
        if ((triggerEl instanceof HTMLInputElement || triggerEl instanceof HTMLSelectElement)
            && triggerEl.hasAttribute("data-autosubmit")
            && triggerEl.form) {
            triggerEl.form.requestSubmit();
        }
    });
}

function CopyAppStateToHtmlDocument(appState: HTMLElement) {
    const newTitle = appState.getAttribute("data-page-title");
    if (newTitle) {
        document.title = newTitle;
    }

    //Keep this set of styling-sensitive attrs in sync with ClientSideAppState.cs AddStylingSensitiveAttributes to avoid FOUC.
    syncAttr(appState, "data-app-context");
    syncAttr(appState, "data-rootorg");
    syncAttr(appState, "data-niet-productie-omgeving");
    syncAttr(appState, "class");
    syncAttr(appState, "lang");

    function syncAttr(appState: HTMLElement, dataAttr: string) {
        const value = appState.getAttribute(dataAttr);
        if (!value) {
            document.documentElement.removeAttribute(dataAttr);
        } else {
            document.documentElement.setAttribute(dataAttr, value);
        }
    }
};

function registerSortButtonListeners(scopeEl: HTMLElement) {
    const sortButtons = scopeEl.querySelectorAll(`.${ProgCss.js_sort_buttons}`);

    if (sortButtons.length > 0) {
        const gntVolgorde = cast(scopeEl.querySelector("#gnt_volgorde"), HTMLInputElement);
        const gntSort = cast(scopeEl.querySelector("#gnt_sort"), HTMLInputElement);

        for (const sortButton of sortButtons) {
            sortButton.addEventListener("click", (evt) => {
                const source = cast(evt.currentTarget, HTMLButtonElement);
                gntVolgorde.value = source.getAttribute("data-sort-volgorde")!;
                gntSort.value = source.getAttribute("data-sort-value")!;
            });
        }
    }
}

function registerPortaalIntekenListeners(scopeEl: HTMLElement) {
    const intekenForm = castOrNull(scopeEl.querySelector("#menus"), HTMLFormElement);

    if (intekenForm != null) {
        addEventListener("beforeunload", unloadMelding);

        const intekenMeldaan = castOrNull(scopeEl.querySelector("#intekenMeldaan"), HTMLInputElement);
        intekenMeldaan?.addEventListener("click",
            () => removeEventListener("beforeunload", unloadMelding));
    }

    const boekdoorPeriodeShowMore = castOrNull(scopeEl.querySelector("#boekdoorPeriodeShowMore"), HTMLElement);
    boekdoorPeriodeShowMore?.addEventListener("click", () => {
        const bronDoel = castOrNull(scopeEl.querySelector("#brondoel"), HTMLElement);
        if (bronDoel != null) {
            toggleDisplay(bronDoel);
        }
    })

    const studentdetailShowMore = castOrNull(scopeEl.querySelector("#studentdetailShowMore"), HTMLElement);
    studentdetailShowMore?.addEventListener("click", (e) => {
        e.preventDefault();
        const uitleg = castOrNull(scopeEl.querySelector("#uitleg"), HTMLElement);
        if (uitleg != null) {
            toggleDisplay(uitleg);
        }
    })

    for (const element of scopeEl.querySelectorAll(`.${ProgCss.js_studentdetailHistorie}`)) {
        element.addEventListener("click", () => {
            const historieSpan = cast(element, HTMLSpanElement);
            const historieElement = cast(scopeEl.querySelector("#" + historieSpan.getAttribute("data-stapid")), HTMLElement);
            toggleDisplay(historieElement);
        });
    }

    const btnKopieer = castOrNull(scopeEl.querySelector("#gnt_btn_kopieer"), HTMLButtonElement);
    const btnVerwijder = castOrNull(scopeEl.querySelector("#gnt_btn_verwijder"), HTMLButtonElement);

    btnKopieer?.addEventListener("click", (e) => {
        e.preventDefault();
        (document as any).genericedit.nieuwbevestig.value = "ja";

        for (const elem of document.getElementsByClassName(ProgCss.js_gekopieer)) {
            if (elem instanceof HTMLElement) {
                elem.classList.add(ProgCss.d_none);
            }
        }

        btnKopieer.classList.add(ProgCss.d_none);
        btnVerwijder?.classList.add(ProgCss.d_none);

        createAndAppendCallout(
            "Velden gekopieerd. Maak eventuele aanpassingen en klik op bewaar om deze op te slaan.",
            CalloutType.Info
        );
    })
}

function registerDataOnclickConfirmMessageListeners(scopeEl: HTMLElement) {
    for (const htmlElement of scopeEl.querySelectorAll("button[data-onclick-confirm-message]")) {
        const button = cast(htmlElement, HTMLButtonElement);

        button.addEventListener("click", (e) => {
            e.preventDefault();

            const confirmMessage = AssertNotNull(htmlElement.getAttribute("data-onclick-confirm-message"));
            createAndShowConfirm(
                vertaal("Opmerking", "Notice"),
                confirmMessage,
                () => AssertNotNull(button.form).requestSubmit(button),
            );
        });
    };
}

function registerPortaalIntekenenToestemmingListeners(scopeEl: HTMLElement) {
    for (const htmlElement of scopeEl.querySelectorAll("[data-confirmable]")) {
        const button = cast(htmlElement, HTMLButtonElement);

        button.addEventListener("click", (e) => {
            const elementsThatRequireConsent = scopeEl.querySelectorAll(`.${ProgCss.js_aanmeld_chk}[data-requires-consent]:checked`);
            if (elementsThatRequireConsent.length == 0) {
                return;
            }

            e.preventDefault();

            const intekenButton = AssertNotNull(document.getElementById("intekenMeldaan"));
            const template = AssertNotNull(intekenButton.querySelector("template"));
            const confirmTitle = AssertNotNull(template.getAttribute("data-confirm-title"));

            const toestemming = document.createElement("div");
            toestemming.classList.add(ProgCss.prg_box);
            const container = cast(scopeEl.getElementsByClassName(ProgCss.prg_container)[0], HTMLElement);
            const box = cast(scopeEl.getElementsByClassName(ProgCss.prg_box)[0], HTMLElement);

            const h3 = document.createElement("h3");
            h3.append(confirmTitle);
            h3.classList.add(ProgCss.dialog_heading);

            const headerDiv = document.createElement("div");
            headerDiv.classList.add(ProgCss.prg_box__header);
            headerDiv.append(h3);

            const contentElement = document.createElement("div");
            contentElement.classList.add(ProgCss.prg_box__content)
            const ul = document.createElement("ul");
            for (const item of elementsThatRequireConsent) {
                const li = document.createElement("li");
                li.textContent = item.getAttribute("data-item-kenmerk");
                ul.append(li);
            }

            contentElement.append(vertaal("Om je in te kunnen tekenen hebben we je toestemming nodig voor:", "To enroll you we need your consent for:"));
            contentElement.append(ul);
            contentElement.append(...template.content.cloneNode(true).childNodes);

            const footerConfirmButton = document.createElement("button");
            footerConfirmButton.append(vertaal("Ik ga akkoord", "I consent"));
            footerConfirmButton.classList.add(ProgCss.btn, ProgCss.btn_primary);
            footerConfirmButton.addEventListener("click", () => {
                AssertNotNull(button.form).requestSubmit(button);
            });

            const footerCancelButton = document.createElement("button");
            footerCancelButton.append(vertaal("Annuleren", "Cancel"));
            footerCancelButton.classList.add(ProgCss.btn, ProgCss.btn_outline_primary);
            footerCancelButton.addEventListener("click", () => {
                toggleDisplay(toestemming);
                toggleDisplay(box);
            });

            const footerDiv = document.createElement("div");
            footerDiv.classList.add(
                ProgCss.prg_box__footer,
                ProgCss.u_flex,
                ProgCss.u_justifyContentCenter,
                ProgCss.u_gapMedium
            );
            footerDiv.append(footerCancelButton);
            footerDiv.append(footerConfirmButton);

            toestemming.appendChild(headerDiv);
            toestemming.appendChild(contentElement);
            toestemming.appendChild(footerDiv);

            toggleDisplay(box);
            container.append(toestemming);
        });
    }
}

function registerGenericPrintListeners(scopeEl: HTMLElement) {
    for (const htmlElement of scopeEl.querySelectorAll("button[data-onclick-print]")) {
        htmlElement.addEventListener("click", () => window.print());
    }
}

function listenForEscapeKeyPresses() {
    document.addEventListener("keydown", evt => {
        if (evt.key == "Escape" && document.forms.length > 0) {
            const form = AssertNotNull(document.forms[0]);
            const tooltipCount = GetOpenedClickableTooltipsCount(form);

            if (tooltipCount == 0) {
                const elementsToClick = document.querySelectorAll("[data-click-on-escape]");
                for (const element of elementsToClick) {
                    const button = castOrNull(element, HTMLButtonElement);
                    button?.click();
                }
            }
        }
    });
}

function ImproveKeyboardAndFocusHandling() {
    document.addEventListener("keydown", e => {
        const target = e.target;
        if (target instanceof HTMLLabelElement
            && target.tabIndex === 0
            && document.activeElement === target
            && (e.key === "Enter" || e.key === " ")) {
            target.click(); // pressing enter or space when a <label> is selected is equivalent to clicking it
            e.preventDefault();
            return;
        }
        if (e.key === "Enter" && (target instanceof HTMLInputElement && target.type === 'checkbox'
            || target instanceof HTMLElement && target.classList.contains(ProgCss.is_shownOnClick) && target.classList.contains(ProgCss.Tooltip_link))) {
            target.click(); // pressing enter when a checkbox is selected is equivalent to clicking it
            e.preventDefault();
            return;
        }
        if (document.activeElement instanceof Element
            && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Tab")
            && document.activeElement.classList.contains(ProgCss.striped_summary__link)) {
            if (e.key !== "Tab") {
                e.preventDefault();
                let currentSelection: Element | null = document.activeElement;
                while (currentSelection) {
                    currentSelection = e.key === "ArrowDown" ? currentSelection.nextElementSibling : currentSelection.previousElementSibling;
                    if (currentSelection instanceof HTMLElement && currentSelection.tabIndex === 0) {
                        currentSelection.focus(); //pressing arrow keys in a context menu selects the previous or next option
                        return;
                    }
                }
            }
        }
    });
}
function toggleDisplay(el: HTMLElement) {
    el.style.display = el.style.display == "none" ? "" : "none";
}

function unloadMelding(e: BeforeUnloadEvent) {
    const aanmeldChecks = document.querySelectorAll(`.${ProgCss.js_aanmeld_chk}:checked`);
    if (aanmeldChecks.length > 0) {
        e.preventDefault();
        e.returnValue = "U heeft een vak geselecteerd, maar nog niet aangemeld."; //tekst wordt vermoedelijk nooit getoond, maar moet wel wat bevatten
    }
}

function reverseDateParts(dateStr: string): string {
    return dateStr.split(/[./-]/).reverse().join("-");
}

function registerDateInputListeners(scopeEl: HTMLElement) {
    const shouldUseFallbackTextInputs = new Date(2010, 11, 31).toLocaleString().startsWith('12') &&
        !location.pathname.startsWith('/uoc/aanmelden');
    //we're in en-US style culture mode, with MM-dd-yyyy date format: don't use native pickers.
    for (const inputEl of scopeEl.getElementsByTagName('input')) {
        if (inputEl.type === 'date') {
            const isEn = inputEl.closest("[lang]")?.getAttribute("lang")?.startsWith("en") || false;

            if (shouldUseFallbackTextInputs) {
                inputEl.type = 'text';
                inputEl.value = inputEl.value.split('-').reverse().join('-');
                inputEl.placeholder = inputEl.placeholder || (isEn ? 'dd-MM-yyyy' : 'dd-mm-jjjj');
                inputEl.pattern = String.raw`\d?\d[\.\/\-]\d?\d[\.\/\-]\d\d\d\d`;
            }

            inputEl.addEventListener("input",
                ev => {
                    let errorMessage = "";
                    if (inputEl.validity.patternMismatch) {
                        errorMessage = (isEn ? "Expected format: " : "Verwacht formaat: ") + inputEl.placeholder;
                    } else if (inputEl.value) {
                        const date = new Date(shouldUseFallbackTextInputs ? reverseDateParts(inputEl.value) : inputEl.value);
                        if (inputEl.min && date < new Date(inputEl.min)) {
                            errorMessage = (isEn ? "Choose a date on or after " : "Kies een datum vanaf ") + reverseDateParts(inputEl.min);
                        } else if (inputEl.max && new Date(inputEl.max) < date) {
                            errorMessage = (isEn ? "Choose a date no later than " : "Kies een datum t/m ") + reverseDateParts(inputEl.max);
                        }
                    }

                    inputEl.setCustomValidity(errorMessage);
                    inputEl.parentElement!.querySelector(`.${ProgCss.invalid_feedback}[data-is-date-auto-check]`)?.remove();
                    if (errorMessage) {
                        const msgEl = document.createElement('div');
                        msgEl.classList.add(ProgCss.invalid_feedback);
                        msgEl.setAttribute("data-is-date-auto-check", "");
                        msgEl.textContent = errorMessage;
                        inputEl.insertAdjacentElement("afterend", msgEl);
                    }
                });

        } else if (inputEl.type === 'time' && shouldUseFallbackTextInputs) {
            inputEl.type = 'text';
            inputEl.pattern = String.raw`\d?\d:\d\d(:\d\d)?`;
        }
    }
}


export enum CalloutType {
    Info,
    Danger,
}

export function createAndAppendCallout(text: string, calloutType: CalloutType) {
    const container = cast(document.querySelector(`.${ProgCss.prg_container}`), HTMLElement);

    const h4 = document.createElement("h4");
    h4.classList.add(ProgCss.header_title);

    switch (calloutType) {
        case CalloutType.Info:
            h4.append(vertaal("Opmerking", "Remark"));
            break;
        case CalloutType.Danger:
            h4.append(vertaal("Foutmelding", "Error"));
            break;
    }

    const callout = document.createElement("div");
    callout.style.cssText = "margin-bottom: var(--length-large);"
    callout.classList.add(ProgCss.prg_callout, ProgCss.js_prg_niet_printen);

    switch (calloutType) {
        case CalloutType.Info:
            callout.classList.add(ProgCss.prg_callout__info);
            break;
        case CalloutType.Danger:
            callout.classList.add(ProgCss.prg_callout__danger);
            break;
    }

    const content = document.createElement("p");
    content.innerText = text;
    callout.append(h4, content);

    container.prepend(callout);
    callout.scrollIntoView();
}

function createAndShowModal(
    title: string,
    contents: HTMLElement,
    buttons: HTMLElement[],
    onClose?: (event: Event) => void) {
    if (buttons.length == 0) {
        throw new Error("At least 1 button is expected");
    }

    const overlayDiv = document.createElement("div");
    function closeModal(event: Event) {
        document.body.removeChild(overlayDiv);
        onClose?.(event);
    };

    const headerCloseButton = document.createElement("span");
    headerCloseButton.classList.add(ProgCss.dialog_button);
    headerCloseButton.addEventListener("click", closeModal);
    headerCloseButton.append(SemanticSymbol.Close.AsNode());

    const h1 = document.createElement("h1");
    h1.append(title);
    h1.classList.add(ProgCss.dialog_heading);
    h1.append(headerCloseButton);

    const headerDiv = document.createElement("div");
    headerDiv.classList.add(ProgCss.dialog_header);
    headerDiv.append(h1);

    const contentDiv = document.createElement("div");
    contentDiv.append(contents);
    contentDiv.classList.add(ProgCss.dialog_content, ProgCss.u_whiteSpacePreWrap);

    const footerDiv = document.createElement("div");
    footerDiv.classList.add(ProgCss.dialog_footer);

    for (const button of buttons) {
        button.addEventListener("click", closeModal);
        footerDiv.append(button);
    }

    const dialogDiv = document.createElement("div");
    dialogDiv.classList.add(ProgCss.dialog);
    dialogDiv.append(headerDiv, contentDiv, footerDiv);

    overlayDiv.appendChild(dialogDiv);
    overlayDiv.tabIndex = -1;
    overlayDiv.classList.add(ProgCss.overlay);
    overlayDiv.addEventListener("keyup", (evt) => {
        if (evt.key == 'Escape') {
            closeModal(evt);
        }
    });

    document.body.append(overlayDiv);
    overlayDiv.focus();
}

export function createAndShowAlert(
    title: string,
    text: string,
    onClose?: () => void) {
    const footerCloseButton = document.createElement("button");
    footerCloseButton.append(vertaal("Sluiten", "Close"));
    footerCloseButton.classList.add(ProgCss.btn, ProgCss.btn_primary);

    if (onClose) {
        footerCloseButton.addEventListener("click", onClose);
    }

    const contentElement = document.createElement("span");
    contentElement.append(text);

    createAndShowModal(title, contentElement, [footerCloseButton], onClose);
}

export function createAndShowConfirmCustom(
    title: string,
    contents: HTMLElement,
    confirmText: string,
    cancelText: string,
    onConfirm: () => void,
    onCancel?: () => void) {
    const footerConfirmButton = document.createElement("button");
    footerConfirmButton.append(confirmText);
    footerConfirmButton.classList.add(ProgCss.btn, ProgCss.btn_primary);
    footerConfirmButton.addEventListener("click", () => {
        onConfirm();
        onCancel = onConfirm = () => { };
    });

    const footerCancelButton = document.createElement("button");
    footerCancelButton.append(cancelText);
    footerCancelButton.classList.add(ProgCss.btn, ProgCss.btn_outline_primary);

    const closeButtons = [footerCancelButton, footerConfirmButton];
    createAndShowModal(title, contents, closeButtons, () => onCancel?.()); // attention: onCancel is mutable
}

export function createAndShowConfirm(
    title: string,
    text: string,
    onConfirm: () => void,
    onCancel?: () => void) {

    const contentElement = document.createElement("span");
    contentElement.append(text);

    createAndShowConfirmCustom(
        title,
        contentElement,
        vertaal("Bevestigen", "Confirm"),
        vertaal("Annuleren", "Cancel"),
        onConfirm,
        onCancel
    );
}
